<template>
    <div
        v-if="showLocaleSwitcher"
        class="flex shrink-0 items-center"
    >
        <!-- Keep one native select across breakpoints, using the globe only while the language label is collapsed. -->
        <label
            class="relative inline-flex items-center rounded focus-within:ring-2 focus-within:ring-primary-500"
            :class="
                expanded
                    ? 'h-7 w-auto border border-gray-300 bg-white shadow-sm focus-within:border-gray-500 hover:border-gray-400'
                    : 'md:hover:bg-transparent h-10 w-10 hover:bg-gray-100 md:w-auto'
            "
        >
            <span class="sr-only">{{ t('layout.localeSwitcher') }}</span>
            <i
                v-if="!expanded"
                class="pi pi-globe pointer-events-none absolute left-3 z-10 text-gray-500 md:hidden"
                aria-hidden="true"
            />
            <!-- Size the expanded control from the active label instead of the longest native select option. -->
            <span
                class="pointer-events-none font-semibold text-gray-600"
                :class="
                    expanded
                        ? 'block py-1 pl-2 pr-7 text-xs'
                        : 'hidden py-2 pl-2 pr-6 text-sm md:block'
                "
                aria-hidden="true"
            >
                {{ selectedLocaleLabel }}
            </span>
            <select
                v-model="selectedLocale"
                class="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            >
                <option
                    v-for="localeOption in localeOptions"
                    :key="localeOption.value"
                    :value="localeOption.value"
                >
                    {{ localeOption.label }}
                </option>
            </select>
            <i
                class="pi pi-angle-down pointer-events-none absolute right-2 text-xs text-gray-400"
                :class="expanded ? 'block' : 'hidden md:block'"
                aria-hidden="true"
            />
        </label>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useCookie } from '#app'
import { useI18n } from 'vue-i18n'
import { useLocalization } from '~/composables/use-localization'
import {
    LOCALE_COOKIE_NAME,
    type SupportedLocale
} from '~/composables/create-language-context'

// Footer placements can keep the active language visible even at compact breakpoints.
defineProps<{
    expanded?: boolean
}>()

const { locale, setLocale, t } = useI18n({ useScope: 'global' })
const { availableLocales } = useLocalization()

// Keep explicit visitor selections for one year so moving the control does not change persistence behavior.
const localeCookieMaxAgeSeconds = 60 * 60 * 24 * 365
const preferredLocale = useCookie<SupportedLocale | undefined>(
    LOCALE_COOKIE_NAME,
    {
        path: '/',
        sameSite: 'lax',
        maxAge: localeCookieMaxAgeSeconds
    }
)

const localeOptions = computed(() => {
    return availableLocales.value
})

const showLocaleSwitcher = computed(() => {
    // A single-language organization does not need to spend header space on a disabled choice.
    return localeOptions.value.length > 1
})

const selectedLocale = computed<SupportedLocale>({
    get() {
        // Keep the responsive native select synchronized with the active storefront locale.
        return locale.value as SupportedLocale
    },
    set(nextLocale) {
        void setPreferredLocale(nextLocale)
    }
})

const selectedLocaleLabel = computed(() => {
    // Render only the current label so short language names do not inherit another option's width.
    return (
        localeOptions.value.find(option => option.value === locale.value)
            ?.label ?? ''
    )
})

async function setPreferredLocale(nextLocale: SupportedLocale) {
    // Write the preference before changing i18n so refreshed locale-aware queries retain the selection.
    preferredLocale.value = nextLocale
    await setLocale(nextLocale)
}
</script>
